import { useI18n } from 'i18n-utils'
import { HocElDialog } from '../hoc-element-components/HocElDialog'
import { useDialog } from '../stores/use-dialog'
import { DialogHeader } from '../components/DialogHeader'
import { ContractCalculation, DefineComponentProps, safeSecondToMillisecond } from 'utils'
import { PositionItemLayout } from '../layouts/PositionInfo.layout'
import { useBaseInfoStore, useDoubleContractAssetsAccountStore, useExchangeInfoStore, useProjectTheme, useQuoteStore } from 'store'
import './OpenPositionDetail.scss'
import { Tag } from '../components/Tag'
import { OrderDirection } from 'network'
import { ListCell } from '../components/ListCell'
import { Pnl } from '../components/Pnl'
import { dayjs } from 'element-plus'
import { TabPane, Tabs } from '../components/Tabs'
import { HistoricalEntrust } from './HistoricalEntrust'
import { HistoricalTrade } from './HistoricalTrade'
import { FundFlow } from './FundFlow'
import { UiUtils } from '../ui-utils/ui-utils'

export type OpenPositionDetailProps = {
  position: ApiResponse.DoubleContractPosition
  onClose?: () => void
}


enum OpenPositionDetailTab {
  Position = 'position',
  Trade = 'trade',
  Fund = 'fund',
}

export const OpenPositionDetail = defineComponent<OpenPositionDetailProps>({
  props: DefineComponentProps<OpenPositionDetailProps>(['position', 'onClose']),
  setup(props) {
    const { openDialog, closeDialog, dialogVisible } = useDialog()
    const themeStore = useProjectTheme()
    const theme = computed(() => themeStore.theme)
    const baseInfoStore = useBaseInfoStore()
    const { t } = useI18n()
    const exchangeStore = useExchangeInfoStore()
    const quoteStore = useQuoteStore()

    const contractAssetsAccountStore = useDoubleContractAssetsAccountStore()
    const activeTab = ref(OpenPositionDetailTab.Position)

    onBeforeMount(() => {
      openDialog()
    })

    const onClose = () => {
      if (!dialogVisible.value) return
      closeDialog()
      props.onClose?.()
    }

    const getMarkPrice = (row: ApiResponse.DoubleContractPosition) => {
      const symbol = quoteStore.getSymbolBySymbolIdWithContractList(row.symbolId)
      const markPrice = symbol?.markPrice
      return markPrice
    }

    const getPnl = (row: ApiResponse.DoubleContractPosition) => {
      const pnl = ContractCalculation.getPnl({
        openPrice: row.holdPrice,
        closePrice: getMarkPrice(row),
        qty: row.qty,
        contractValue: row.contractValue,
        dir: row.direction.toString(),
      })
      return pnl.cutDecimal(2)
    }

    const onTooltipClick = () => {
      UiUtils.showModal(
        <div class="open-position-detail-pos-info position-info">
          <ListCell title={t('已实现盈亏')} description={<Pnl value={props.position.rlzPnl}>USDT</Pnl>} />
          <div class="open-position-detail-card-bg card-bg" style={{ marginTop: '12px' }}>
            <ListCell title={t('平仓盈亏')} description={<Pnl value={props.position.positionPnl}>USDT</Pnl>} />
            <ListCell title={t('手续费')} description={`${props.position.totalFee.formatCommonMoney()} USDT`} />
            <ListCell title={t('资金费用')} description={`${props.position.fundFee.formatCommonMoney()} USDT`} />
            <ListCell title={t('风险基金')} description={`${props.position.totalFund.formatCommonMoney()} USDT`} />
          </div>

          <p class={'tips'}>{t('已实现盈亏为已经结算到用户余额中的收益，包含平仓盈亏、交易手续费、资金费用、风险基金(强平收取)。')}</p>
        </div>,
        {
          title: t('已实现盈亏'),
          width: 430,
        },
      )
    }

    return () => (
      <HocElDialog v-model={dialogVisible.value} showClose={false} width={1200} onClose={onClose} placement="right" offsetTop={0} class="header-pb-0">
        {{
          header: () => <DialogHeader onClose={onClose}>{t('仓位详情')}</DialogHeader>,
          default: () => (
            <div class="open-position-detail-layout">
              <PositionItemLayout style={{ borderBottom: 'none' }}>
                <div class="symbol-coin-info">
                  <img width={32} height={32} src={baseInfoStore.getContractCoinIconUrlBySymbolId(props.position.symbolId)} alt="coin icon" />
                  <div class="coin-info">
                    <div class="coin-name">
                      {exchangeStore.getContractSymbolName(props.position.symbolId)} {t('永续')}
                    </div>
                    <div class="flex info-tags gap-4">
                      <Tag color={props.position.direction === OrderDirection.Buy ? theme.value.colors.up : theme.value.colors.down}>
                        {props.position.direction === OrderDirection.Buy ? t('多') : t('空')}
                      </Tag>
                      <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                        {props.position.marginType === 1 ? t('逐仓') : t('全仓')}
                        {props.position.leverage}X
                      </Tag>
                    </div>
                  </div>
                  <div class="status up">{t('持仓中')}</div>
                </div>

                <div class="position-info">
                  <ListCell title={t('未实现盈亏')} description={<Pnl value={getPnl(props.position)}>USDT</Pnl>} />
                  <ListCell title={t('已实现盈亏')} isShowTooltip description={<Pnl value={props.position.rlzPnl}>USDT</Pnl>} onTooltipClick={onTooltipClick} />
                  <ListCell title={t('开仓均价')} description={props.position.holdPrice.formatNumberWithCommas()} />

                  <ListCell
                    title={t('持仓数量')}
                    description={`${props.position.positionAmt.formatCommonMoney()} ${exchangeStore.getFromSymbolNameBySymbolId(props.position.symbolId)} `}
                  />

                  <ListCell
                    title={t('平仓数量')}
                    description={`${contractAssetsAccountStore.getCloseInterest(props.position).formatCommonMoney()} ${exchangeStore.getFromSymbolNameBySymbolId(props.position.symbolId)}`}
                  />
                  <ListCell title={t('开仓时间')} description={dayjs(safeSecondToMillisecond(props.position.openTime)).format('YYYY-MM-DD HH:mm:ss')} />

                  <ListCell title={t('订单号')} description={props.position.orderNo} isCopy />
                </div>

                <div class="open-position-detail-card-bg card-bg">
                  <div class="position-info">
                    <ListCell title={t('已实现盈亏')} description={<Pnl value={props.position.rlzPnl}>USDT</Pnl>} />
                    <ListCell title={t('平仓盈亏')} description={<Pnl value={props.position.positionPnl}>USDT</Pnl>} />
                    <ListCell title={t('手续费')} description={`${props.position.totalFee.formatCommonMoney()} USDT`} />
                    <ListCell title={t('资金费用')} description={(props.position.fundFee >= 0 ? '+' : '') + `${props.position.fundFee.formatCommonMoney()} USDT`} />
                    <ListCell title={t('风险基金')} description={`${props.position.totalFund.formatCommonMoney()} USDT`} />
                  </div>
                </div>
              </PositionItemLayout>

              <Tabs tabHeight={42} v-model={activeTab.value}>
                <TabPane label={t('委托记录')} name={OpenPositionDetailTab.Position}>
                  <HistoricalEntrust positionId={props.position.positionId.toString()} emptyAreaHeight={300} />
                </TabPane>
                <TabPane label={t('成交记录')} name={OpenPositionDetailTab.Trade}>
                  <HistoricalTrade positionId={props.position.positionId.toString()} />
                </TabPane>
                <TabPane label={t('资金流水')} name={OpenPositionDetailTab.Fund}>
                  <FundFlow positionId={props.position.positionId.toString()} symbolId={props.position.symbolId} />
                </TabPane>
              </Tabs>
            </div>
          ),
        }}
      </HocElDialog>
    )
  },
})
